"use client";

import {
  Box,
  Button,
  HStack,
  Text,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import GenericIcon, { type GenericIconType } from "../actions/GenericIcon";

export type GenericInteractiveGuidanceProps = {
  title: ReactNode;
  message: ReactNode;
  compactMessage?: ReactNode;
  icon: GenericIconType;
  accentColor: string;
  accentBg: string;
  accentBorderColor: string;
  dismissLabel?: string;
  onDismiss?: () => void;
};

export default function GenericInteractiveGuidance({
  title,
  message,
  compactMessage,
  icon,
  accentColor,
  accentBg,
  accentBorderColor,
  dismissLabel = "Dismiss guidance",
  onDismiss,
}: GenericInteractiveGuidanceProps) {
  const mobileMessage = compactMessage ?? message;
  const iconLabel = typeof title === "string" ? title : "Guidance";

  return (
    <Box
      borderWidth="1"
      borderLeftWidth="3"
      borderColor={accentBorderColor}
      borderLeftColor={accentColor}
      borderRadius="md"
      bg={accentBg}
      px={{ base: "2.5", md: "3" }}
      py={{ base: "2", md: "2.5" }}
      mb={{ base: "2", md: "3" }}
    >
      <HStack gap="2.5" align="center">
        <Box flexShrink={0} color={accentColor}>
          <GenericIcon icon={icon} humanReadable={iconLabel} size="sm" />
        </Box>

        <Box minW="0" flex="1">
          <Text
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="semibold"
            color="moveon.surface.fg"
            lineHeight="short"
          >
            {title}
          </Text>
          <Text
            fontSize="sm"
            color="moveon.text.muted"
            lineHeight="tall"
            display={{ base: "none", sm: "block" }}
          >
            {message}
          </Text>
          <Text
            fontSize="xs"
            color="moveon.text.muted"
            lineHeight="tall"
            display={{ base: "block", sm: "none" }}
          >
            {mobileMessage}
          </Text>
        </Box>

        {onDismiss ? (
          <Button
            variant="ghost"
            size="sm"
            minW="8"
            px="2"
            color={accentColor}
            aria-label={dismissLabel}
            title={dismissLabel}
            onClick={onDismiss}
            _hover={{ bg: accentBg }}
          >
            <GenericIcon icon="close" humanReadable={dismissLabel} size="xs" />
          </Button>
        ) : null}
      </HStack>
    </Box>
  );
}
